import type { WeaponKind } from '../data/mockLots';

type Palette = { from: string; via?: string; to: string; accent?: string };

type Props = {
  weapon: WeaponKind;
  palette: Palette;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showPattern?: boolean;
  showWeapon?: boolean;
};

const weaponPath: Record<WeaponKind, string> = {
  sniper:
    'M10 64 L30 64 L40 58 L80 56 L130 54 L180 53 L200 52 L240 51 L270 53 L286 55 L286 60 L270 62 L240 63 L200 64 L182 66 L172 70 L172 76 L160 76 L156 70 L130 70 L80 70 L40 70 L30 70 L10 70 Z',
  rifle:
    'M16 60 L40 58 L60 56 L92 56 L120 54 L170 53 L200 53 L226 55 L250 58 L264 62 L264 70 L226 72 L200 72 L170 72 L160 76 L150 84 L140 84 L135 76 L120 72 L92 72 L72 76 L60 78 L40 76 L16 72 Z',
  pistol:
    'M70 60 L110 58 L150 58 L182 60 L196 64 L196 72 L184 76 L176 88 L168 100 L156 102 L148 96 L150 80 L130 76 L110 76 L80 76 L70 72 Z',
  knife:
    'M40 80 L80 70 L130 56 L180 46 L220 40 L240 42 L240 50 L218 56 L182 64 L140 76 L100 90 L70 96 L48 92 Z M40 80 L48 92 L36 96 L28 90 Z',
  smg: 'M30 64 L60 60 L90 58 L130 58 L170 60 L200 64 L200 72 L170 76 L160 86 L150 86 L146 76 L130 76 L90 76 L60 80 L40 78 L30 72 Z',
};

const sizeMap = {
  sm: { w: 320, h: 160, viewBox: '0 0 320 160' },
  md: { w: 360, h: 220, viewBox: '0 0 360 220' },
  lg: { w: 400, h: 280, viewBox: '0 0 400 280' },
};

export function SkinFrame({
  weapon,
  palette,
  size = 'md',
  className = '',
  showPattern = true,
  showWeapon = true,
}: Props) {
  const { w, h, viewBox } = sizeMap[size];
  const id = `skin-${weapon}-${palette.from.replace('#', '')}-${size}`;
  const path = weaponPath[weapon];

  return (
    <svg
      viewBox={viewBox}
      preserveAspectRatio="xMidYMid slice"
      className={className}
      width="100%"
      height="100%"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={`${id}-bg`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={palette.from} />
          {palette.via && <stop offset="55%" stopColor={palette.via} />}
          <stop offset="100%" stopColor={palette.to} />
        </linearGradient>

        <linearGradient id={`${id}-weapon`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#0a0a0a" />
          <stop offset="50%" stopColor={palette.accent ?? palette.to} stopOpacity="0.85" />
          <stop offset="100%" stopColor="#1a1a1a" />
        </linearGradient>

        <pattern
          id={`${id}-stripes`}
          width="14"
          height="14"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(28)"
        >
          <line
            x1="0"
            y1="0"
            x2="0"
            y2="14"
            stroke={palette.accent ?? palette.to}
            strokeWidth="1.2"
            strokeOpacity="0.22"
          />
        </pattern>

        <radialGradient id={`${id}-glow`} cx="50%" cy="55%" r="60%">
          <stop offset="0%" stopColor={palette.accent ?? palette.to} stopOpacity="0.5" />
          <stop offset="100%" stopColor={palette.accent ?? palette.to} stopOpacity="0" />
        </radialGradient>

        <filter id={`${id}-blur`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="2" />
        </filter>
      </defs>

      <rect width="100%" height="100%" fill={`url(#${id}-bg)`} />
      {showPattern && <rect width="100%" height="100%" fill={`url(#${id}-stripes)`} />}

      <rect width="100%" height={h * 0.7} y={h * 0.15} fill={`url(#${id}-glow)`} />

      {showWeapon && (
        <g
          transform={
            weapon === 'pistol'
              ? `translate(${w / 2 - 130}, ${h / 2 - 80}) scale(${w / 360})`
              : weapon === 'knife'
                ? `translate(${w / 2 - 140}, ${h / 2 - 70}) scale(${w / 360})`
                : `translate(${w / 2 - 148}, ${h / 2 - 60}) scale(${w / 320})`
          }
        >
          <path
            d={path}
            fill={`url(#${id}-weapon)`}
            stroke={palette.accent ?? palette.to}
            strokeWidth="1.4"
            strokeOpacity="0.85"
            filter={`url(#${id}-blur)`}
          />
          <path
            d={path}
            fill="none"
            stroke={palette.accent ?? palette.to}
            strokeWidth="1"
            strokeOpacity="0.65"
          />
          <path d={path} fill="#000" fillOpacity="0.55" />
        </g>
      )}

      <rect x="0" y="0" width="100%" height="100%" fill="none" stroke="rgba(255,255,255,0.05)" />

      {[6, 24].map((y) => (
        <line
          key={`h-${y}`}
          x1="0"
          x2="14"
          y1={y}
          y2={y}
          stroke={palette.accent ?? palette.to}
          strokeOpacity="0.6"
          strokeWidth="1"
        />
      ))}
      {[h - 6, h - 24].map((y) => (
        <line
          key={`hb-${y}`}
          x1={w - 14}
          x2={w}
          y1={y}
          y2={y}
          stroke={palette.accent ?? palette.to}
          strokeOpacity="0.6"
          strokeWidth="1"
        />
      ))}
    </svg>
  );
}
